import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import {
  CLAUDIA_ATTACHMENT_MAX_PER_PROMPT,
  type ClaudiaAttachmentCategory,
  type ClaudiaAttachmentKind,
  isMediaArtifactFile,
  resolveClaudiaAttachmentFileType,
} from "@repo/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useObservedAction, useObservedMutation } from "@/hooks/use-observed-convex";
import { getErrorMessage } from "@/lib/app-errors";
import { prepareClaudiaAttachmentFile } from "@/lib/attachment-processing";
import { uploadToConvexStorage } from "../lib/upload-to-convex-storage";
import { uploadToR2 } from "../lib/upload-to-r2";

export type ClaudiaComposerAttachment = {
  localId: string;
  attachmentId?: Id<"assistantAttachments">;
  fileName: string;
  mimeType: string;
  kind: ClaudiaAttachmentKind;
  category?: ClaudiaAttachmentCategory;
  fileSizeBytes: number;
  width?: number;
  height?: number;
  previewUrl: string;
  uploadProgress: number;
  status: "uploading" | "ready" | "failed";
  error?: string;
};

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === "AbortError";
}

export function useClaudiaAttachments(args: {
  projectId: Id<"projects">;
  threadId: string | null;
  disabled?: boolean;
}) {
  const [attachments, setAttachments] = useState<ClaudiaComposerAttachment[]>([]);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  // The composer's scope is the project *and* the thread: the same thread id under another
  // project is another conversation, and leaving either one abandons the drafts held here.
  const composerScope = `${args.projectId}:${args.threadId ?? ""}`;
  const previousScopeRef = useRef(composerScope);

  // A draft the person lets go of — removed from the composer, or dropped by a thread change or an
  // unmount — takes its upload with it. The controller aborts the bytes still in flight and the
  // local id is marked discarded, so the rest of the upload stops before it writes a delivery row,
  // an artifact and a processing job for a chip nobody can see. Holding a controller is also what
  // says an upload is still running: an id can only be discarded while one exists, and it is
  // forgotten in the same `finally` that drops the controller, so neither collection outlives the
  // upload it describes.
  const uploadAbortsRef = useRef(new Map<string, AbortController>());
  const discardedUploadsRef = useRef(new Set<string>());
  /**
   * What every drafted chip was registered as, and where, written in the same tick registration
   * resolves — a render before the chip itself carries the id. A removal that lands in that gap
   * reads state that has not committed yet, and reading only that state would drop the chip while
   * leaving the delivery row, the artifact and its processing behind. The project is part of the
   * record because a draft is removed in the project that minted it: this hook instance follows
   * the surface from one project to the next, so the project of the render doing the cleaning up
   * is not the project the drafts belong to.
   */
  const registeredAttachmentsRef = useRef(
    new Map<string, { attachmentId: Id<"assistantAttachments">; projectId: Id<"projects"> }>()
  );
  /**
   * The local ids the composer is holding, reserved in the same tick as the state write. Two drops
   * in one tick both read `attachments` before either of them renders, so counting the chips on
   * screen let each of them claim the same free slots — and the send then failed against the
   * server's own draft ceiling, which is higher than the per-prompt one.
   */
  const reservedSlotsRef = useRef(new Set<string>());
  /**
   * Set while a send is carrying these drafts. The rows are linked to the message inside the
   * submit transaction, so until it answers they are still unlinked drafts that `discard` would
   * happily take back out from under the message claiming them.
   */
  const submissionInFlightRef = useRef(false);

  const createUpload = useObservedMutation(api.claudia.createAttachmentUpload, {
    feature: "assistant",
    operation: "create-attachment-upload",
    includeRequestId: true,
  });
  const registerAttachment = useObservedMutation(api.claudia.registerAttachment, {
    feature: "assistant",
    operation: "register-attachment",
    includeRequestId: true,
  });
  // The recording pair, and they are `artifactMedia.*` rather than `claudia.*` because the
  // R2 seam is held by one entry module: the composer calls it directly, exactly as the
  // Documents upload queue does.
  const createMediaUpload = useObservedMutation(
    api.artifactMedia.generateAttachmentMediaUploadUrl,
    {
      feature: "assistant",
      operation: "create-attachment-media-upload",
      includeRequestId: true,
    }
  );
  const createMediaAttachment = useObservedAction(api.artifactMedia.createMediaAttachment, {
    feature: "assistant",
    operation: "create-media-attachment",
    includeRequestId: true,
    // Safe to re-run: `registerMediaAttachment` is idempotent on the thread and `r2Key`.
    retryOnConnectionLoss: true,
  });
  const removeDraftAttachment = useObservedMutation(api.claudia.removeDraftAttachment, {
    feature: "assistant",
    operation: "remove-draft-attachment",
    includeRequestId: true,
  });

  const discardUpload = useCallback((localId: string) => {
    const controller = uploadAbortsRef.current.get(localId);
    if (!controller) {
      return;
    }
    discardedUploadsRef.current.add(localId);
    controller.abort();
  }, []);

  const discardDraftAttachment = useCallback(
    async (attachmentId: Id<"assistantAttachments">, projectId: Id<"projects">) => {
      await removeDraftAttachment({ projectId, attachmentId }).catch(() => undefined);
    },
    [removeDraftAttachment]
  );

  const forgetAttachment = useCallback((localId: string) => {
    reservedSlotsRef.current.delete(localId);
    registeredAttachmentsRef.current.delete(localId);
  }, []);

  /**
   * Everything the composer holds locally: the chips, their previews, the slots they reserved and
   * any transfer still moving for them. The drafts already registered are left where they are,
   * because this is what runs once a send has taken them — those rows now belong to a message.
   */
  const reset = useCallback(() => {
    for (const localId of uploadAbortsRef.current.keys()) {
      discardUpload(localId);
    }
    reservedSlotsRef.current.clear();
    registeredAttachmentsRef.current.clear();
    setAttachments((current) => {
      for (const attachment of current) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
      return [];
    });
  }, [discardUpload]);

  /** Held for as long as a send is carrying the drafts, which is one round trip. */
  const setSubmissionInFlight = useCallback((inFlight: boolean) => {
    submissionInFlightRef.current = inFlight;
  }, []);

  /**
   * The other ending: the composer was abandoned — the conversation changed, or the surface went
   * away — so every draft it holds is a delivery nobody will send, and each one is taken back
   * rather than left to the draft sweep a day later, still transcribing a recording for a message
   * that does not exist. The two endings are told apart here rather than at the server: a linked
   * row is refused, so a `discard` on the way out of a send would fire refused mutations and a
   * `reset` on the way out of the composer would leave the drafts standing.
   *
   * While a send is in flight the local half still runs and the server half does not: those rows
   * are being claimed by a message this instant, and a row a failed send leaves unlinked is what
   * the draft sweep is for.
   */
  const discard = useCallback(() => {
    // The ref is the authority on what has been registered: it is written in the same tick as the
    // id, a render before any chip carries it, and it names the project each draft was minted in.
    const registered = [...registeredAttachmentsRef.current.values()];
    const isSubmitting = submissionInFlightRef.current;
    reset();
    if (isSubmitting) {
      return;
    }
    for (const draft of registered) {
      void discardDraftAttachment(draft.attachmentId, draft.projectId);
    }
  }, [discardDraftAttachment, reset]);

  // Read through a ref: an effect that took `discard` as a dependency would run its cleanup on
  // every identity change rather than on the way out, which is the composer throwing away the
  // drafts of the message being written in it.
  const discardRef = useRef(discard);
  discardRef.current = discard;

  useEffect(() => {
    if (previousScopeRef.current === composerScope) {
      return;
    }
    previousScopeRef.current = composerScope;
    discardRef.current();
  }, [composerScope]);

  useEffect(() => () => discardRef.current(), []);

  const removeAttachment = useCallback(
    async (localId: string) => {
      const registered = registeredAttachmentsRef.current.get(localId);
      forgetAttachment(localId);
      discardUpload(localId);
      setAttachments((current) =>
        current.filter((attachment) => {
          if (attachment.localId === localId) {
            URL.revokeObjectURL(attachment.previewUrl);
            return false;
          }
          return true;
        })
      );
      if (registered) {
        await discardDraftAttachment(registered.attachmentId, registered.projectId);
      }
    },
    [discardDraftAttachment, discardUpload, forgetAttachment]
  );

  const addFiles = useCallback(
    async (files: File[]) => {
      if (args.disabled || !args.threadId || files.length === 0) {
        return;
      }
      // Captured once: everything below outlives several renders, and a file is minted,
      // registered and — if it is let go of — taken back in the project and thread it was
      // attached to, whatever the surface has moved on to since.
      const projectId = args.projectId;
      const threadId = args.threadId;

      const remaining = CLAUDIA_ATTACHMENT_MAX_PER_PROMPT - reservedSlotsRef.current.size;
      if (remaining <= 0) {
        toast.error(`Attach up to ${CLAUDIA_ATTACHMENT_MAX_PER_PROMPT} files per message.`);
        return;
      }

      // The whole batch is claimed before the first `await` — a slot and a controller each. The
      // loop below takes one file at a time, so a batch that only armed the file it had reached
      // could be let go of and still go on drafting, minting and registering its siblings, into
      // the conversation the person had left.
      const queued = files.slice(0, remaining).map((sourceFile) => {
        const localId = crypto.randomUUID();
        reservedSlotsRef.current.add(localId);
        const abortController = new AbortController();
        uploadAbortsRef.current.set(localId, abortController);
        return { abortController, localId, sourceFile };
      });

      for (const { abortController, localId, sourceFile } of queued) {
        if (abortController.signal.aborted || discardedUploadsRef.current.has(localId)) {
          uploadAbortsRef.current.delete(localId);
          discardedUploadsRef.current.delete(localId);
          forgetAttachment(localId);
          continue;
        }
        const previewUrl = URL.createObjectURL(sourceFile);
        const initialType = resolveClaudiaAttachmentFileType(sourceFile.name, sourceFile.type);
        setAttachments((current) => [
          ...current,
          {
            localId,
            fileName: sourceFile.name,
            mimeType: sourceFile.type || "application/octet-stream",
            kind:
              initialType?.kind ?? (sourceFile.type.startsWith("image/") ? "image" : "document"),
            category: initialType?.category,
            fileSizeBytes: sourceFile.size,
            previewUrl,
            uploadProgress: 0,
            status: "uploading",
          },
        ]);

        try {
          const prepared = await prepareClaudiaAttachmentFile(sourceFile);
          const uploadFile = prepared.kind === "image" ? prepared.asset.original : prepared.file;
          const modelVariant = prepared.kind === "image" ? prepared.asset.modelVariant : undefined;
          const originalWidth =
            prepared.kind === "image" ? prepared.asset.originalWidth : undefined;
          const originalHeight =
            prepared.kind === "image" ? prepared.asset.originalHeight : undefined;
          const reportProgress = (percent: number) => {
            setAttachments((current) =>
              current.map((attachment) =>
                attachment.localId === localId
                  ? { ...attachment, uploadProgress: percent }
                  : attachment
              )
            );
          };
          const wasDiscarded = () =>
            abortController.signal.aborted || discardedUploadsRef.current.has(localId);

          // Two stores, one chip. A recording's bytes go browser → media storage over a
          // create-only presigned PUT and never enter a Convex function; everything else takes
          // the Convex storage path. `isMediaArtifactFile` is the only routing question, and it
          // is the same one the Documents upload queue asks — the Convex-storage mint refuses a
          // recording outright, so the two answers cannot drift apart unnoticed.
          if (isMediaArtifactFile(uploadFile.name, uploadFile.type)) {
            const upload = await createMediaUpload({
              projectId,
              threadId,
              name: uploadFile.name,
              fileSizeBytes: uploadFile.size,
              mimeType: uploadFile.type || undefined,
            });
            await uploadToR2(
              upload.url,
              uploadFile,
              (fraction) => reportProgress(Math.round(fraction * 100)),
              abortController.signal
            );
            // Stopping here leaves an object under a ledgered key that nothing registered, which
            // is what the sweep exists for — registering it would leave a delivery row, an
            // artifact and a transcription job behind a chip that is already gone.
            if (wasDiscarded()) {
              continue;
            }
            const registeredMedia = await createMediaAttachment(
              {
                projectId,
                threadId,
                // The key comes from the mutation that authorised the upload, never from the
                // client: it names the project, and the server re-checks that it does.
                r2Key: upload.key,
                name: uploadFile.name,
                fileSizeBytes: uploadFile.size,
                mimeType: uploadFile.type || undefined,
              },
              { signal: abortController.signal }
            );
            registeredAttachmentsRef.current.set(localId, {
              attachmentId: registeredMedia.attachmentId,
              projectId,
            });
            // Registration can win the race. A call already in flight cannot be recalled, so a
            // draft that lands after its chip is gone is undone on the server rather than
            // prevented here, and never written back into state.
            if (wasDiscarded()) {
              registeredAttachmentsRef.current.delete(localId);
              await discardDraftAttachment(registeredMedia.attachmentId, projectId);
              continue;
            }
            setAttachments((current) =>
              current.map((attachment) =>
                attachment.localId === localId
                  ? {
                      ...attachment,
                      attachmentId: registeredMedia.attachmentId,
                      fileName: uploadFile.name,
                      mimeType: prepared.mimeType,
                      kind: prepared.kind,
                      category: prepared.category,
                      fileSizeBytes: uploadFile.size,
                      uploadProgress: 100,
                      status: "ready",
                    }
                  : attachment
              )
            );
            continue;
          }

          const [{ uploadUrl }, modelVariantUpload] = await Promise.all([
            createUpload({
              projectId,
              threadId,
              fileName: uploadFile.name,
              fileSizeBytes: uploadFile.size,
              mimeType: uploadFile.type || undefined,
            }),
            modelVariant
              ? // No content type: the model variant is a re-encode that keeps the original's
                // name, so its bytes and its extension legitimately disagree. What it must be
                // is checked against the stored blob when it is registered.
                createUpload({
                  projectId,
                  threadId,
                  fileName: modelVariant.file.name,
                  fileSizeBytes: modelVariant.file.size,
                }).catch(() => null)
              : Promise.resolve(null),
          ]);
          const originalStorageFileIdPromise = uploadToConvexStorage(
            uploadUrl,
            uploadFile,
            (fraction) => {
              const percent = fraction * 100;
              reportProgress(modelVariant ? Math.min(90, Math.round(percent * 0.9)) : percent);
            },
            abortController.signal
          );
          const modelVariantStorageFileIdPromise =
            modelVariant && modelVariantUpload
              ? uploadToConvexStorage(
                  modelVariantUpload.uploadUrl,
                  modelVariant.file,
                  (fraction) => {
                    reportProgress(Math.max(90, Math.min(99, fraction * 100)));
                  },
                  abortController.signal
                )
                  .catch(() => undefined)
                  .then((storageFileId) => storageFileId as Id<"_storage"> | undefined)
              : Promise.resolve(undefined);
          const [storageFileId, modelVariantStorageFileId] = await Promise.all([
            originalStorageFileIdPromise,
            modelVariantStorageFileIdPromise,
          ]);
          // An unused storage blob is the sweep's business; a registered attachment behind a
          // chip that is gone is nobody's.
          if (wasDiscarded()) {
            continue;
          }
          const registered = await registerAttachment({
            projectId,
            threadId,
            storageFileId: storageFileId as Id<"_storage">,
            fileName: uploadFile.name,
            width: originalWidth,
            height: originalHeight,
            modelVariantStorageFileId,
            modelVariantMimeType: modelVariantStorageFileId ? modelVariant?.file.type : undefined,
            modelVariantWidth: modelVariantStorageFileId ? modelVariant?.width : undefined,
            modelVariantHeight: modelVariantStorageFileId ? modelVariant?.height : undefined,
          });
          registeredAttachmentsRef.current.set(localId, {
            attachmentId: registered.attachmentId,
            projectId,
          });
          if (wasDiscarded()) {
            registeredAttachmentsRef.current.delete(localId);
            await discardDraftAttachment(registered.attachmentId, projectId);
            continue;
          }

          setAttachments((current) =>
            current.map((attachment) =>
              attachment.localId === localId
                ? {
                    ...attachment,
                    attachmentId: registered.attachmentId,
                    fileName: uploadFile.name,
                    mimeType: prepared.mimeType,
                    kind: prepared.kind,
                    category: prepared.category,
                    fileSizeBytes: uploadFile.size,
                    width: originalWidth,
                    height: originalHeight,
                    uploadProgress: 100,
                    status: "ready",
                  }
                : attachment
            )
          );
        } catch (error) {
          // A transfer the person stopped is not a failure: the chip it reported through is
          // already gone, so there is no state to write and nothing to say.
          if (isAbortError(error) || discardedUploadsRef.current.has(localId)) {
            continue;
          }
          // A coded refusal — no media bucket on this deployment, a type or a ceiling the
          // server measures by — is a sentence somebody wrote for the person, and the chip is
          // where they read it. Anything else keeps the thrown message, so a browser-side
          // refusal and a network failure still say what happened rather than a generic line.
          const message = getErrorMessage(
            error,
            error instanceof Error ? error.message : "File upload failed"
          );
          setAttachments((current) =>
            current.map((attachment) =>
              attachment.localId === localId
                ? { ...attachment, status: "failed", error: message }
                : attachment
            )
          );
          toast.error(message);
        } finally {
          uploadAbortsRef.current.delete(localId);
          discardedUploadsRef.current.delete(localId);
        }
      }
    },
    [
      args.disabled,
      args.projectId,
      args.threadId,
      createMediaAttachment,
      createMediaUpload,
      createUpload,
      discardDraftAttachment,
      forgetAttachment,
      registerAttachment,
    ]
  );

  const attachmentIds = useMemo(
    () =>
      attachments
        .filter(
          (
            attachment
          ): attachment is ClaudiaComposerAttachment & {
            attachmentId: Id<"assistantAttachments">;
          } => attachment.status === "ready" && Boolean(attachment.attachmentId)
        )
        .map((attachment) => attachment.attachmentId),
    [attachments]
  );

  return {
    attachments,
    attachmentIds,
    addFiles,
    removeAttachment,
    reset,
    setSubmissionInFlight,
    isUploading: attachments.some((attachment) => attachment.status === "uploading"),
    hasFailed: attachments.some((attachment) => attachment.status === "failed"),
    hasAttachments: attachments.length > 0,
  };
}
